/**
 * useRelatedData Hook
 * 
 * Fetches related data for Pointer and Relation fields.
 * - Pointer: Fetches a single object by objectId
 * - Relation: Fetches all objects in a relation using $relatedTo query
 * 
 * Used by RelatedDataModal to display linked objects inline.
 */

import { useState, useEffect, useCallback } from 'react';
import { ParseRow } from '../types/AppPanel';

/** Type of related data to fetch */
export type RelatedDataType = 'pointer' | 'relation';

interface UseRelatedDataParams {
  /** Parse Server URL */
  parseServerURL?: string;
  /** Application ID */
  appId?: string;
  /** Master Key */
  masterKey?: string;
  /** Type of related data (pointer or relation) */
  type: RelatedDataType;
  /** Target class name (class of the related object(s)) */
  targetClass?: string;
  /** Object ID for pointer lookup */
  objectId?: string;
  /** Source class name (for relation queries) */
  sourceClass?: string;
  /** Source object ID (for relation queries) */
  sourceObjectId?: string;
  /** Relation field name (for relation queries) */
  relationField?: string;
  /** Whether to fetch data (set to true when modal opens) */
  enabled?: boolean;
}

interface UseRelatedDataResult {
  /** Single object (for pointer) or array of objects (for relation) */
  data: ParseRow | ParseRow[] | null;
  /** Total count of related objects */
  count: number;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch the data */
  refetch: () => void;
}

/**
 * Hook to fetch related data (Pointer or Relation)
 */
export function useRelatedData({
  parseServerURL,
  appId,
  masterKey,
  type,
  targetClass,
  objectId,
  sourceClass,
  sourceObjectId,
  relationField,
  enabled = false,
}: UseRelatedDataParams): UseRelatedDataResult {
  const [data, setData] = useState<ParseRow | ParseRow[] | null>(null);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch a single object by ID (for Pointer)
   */
  const fetchPointer = useCallback(async () => {
    if (!parseServerURL || !appId || !masterKey || !targetClass || !objectId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const baseUrl = parseServerURL.replace(/\/+$/, '');
      
      const response = await fetch(
        `${baseUrl}/classes/${targetClass}/${objectId}`,
        {
          method: 'GET',
          headers: {
            'X-Parse-Application-Id': appId,
            'X-Parse-Master-Key': masterKey,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          setError('Object not found');
          setData(null);
          return;
        }
        throw new Error(`Failed to fetch object: ${response.status}`);
      }

      const obj = await response.json();
      setData(obj);
      setCount(1);
    } catch (err) {
      console.error('Error fetching pointer data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [parseServerURL, appId, masterKey, targetClass, objectId]);

  /**
   * Fetch related objects (for Relation)
   * Uses Parse's $relatedTo query constraint
   */
  const fetchRelation = useCallback(async () => {
    if (!parseServerURL || !appId || !masterKey || !targetClass || !sourceClass || !sourceObjectId || !relationField) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const baseUrl = parseServerURL.replace(/\/+$/, '');
      
      // Build $relatedTo query
      const where = {
        $relatedTo: {
          object: {
            __type: 'Pointer',
            className: sourceClass,
            objectId: sourceObjectId,
          },
          key: relationField,
        },
      };

      const params = new URLSearchParams({
        where: JSON.stringify(where),
        limit: '100',
        count: '1',
        order: '-createdAt',
      });

      const response = await fetch(
        `${baseUrl}/classes/${targetClass}?${params}`,
        {
          method: 'GET',
          headers: {
            'X-Parse-Application-Id': appId,
            'X-Parse-Master-Key': masterKey,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch relation: ${response.status}`);
      }

      const result = await response.json();
      setData(result.results || []);
      setCount(result.count ?? result.results?.length ?? 0);
    } catch (err) {
      console.error('Error fetching relation data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [parseServerURL, appId, masterKey, targetClass, sourceClass, sourceObjectId, relationField]);

  /**
   * Main fetch function based on type
   */
  const fetchData = useCallback(() => {
    if (type === 'pointer') {
      fetchPointer();
    } else {
      fetchRelation();
    }
  }, [type, fetchPointer, fetchRelation]);

  // Fetch when enabled and params are ready
  useEffect(() => {
    if (enabled) {
      fetchData();
    } else {
      // Reset when disabled
      setData(null);
      setCount(0);
      setError(null);
    }
  }, [enabled, fetchData]);

  return {
    data,
    count,
    isLoading,
    error,
    refetch: fetchData,
  };
}

export default useRelatedData;

